import React, { useMemo } from 'react';
import dayjs from 'dayjs';
import { Box, Paper, Typography, Tooltip } from '@mui/material';
import {
  expandRecurringMeetings,
  taskToMeetingEvent,
  buildMonthDays,
  DAY_COLORS,
} from './calendarUtils';

/**
 * Month calendar grid. Each day shows a meeting count and tasks-due count,
 * colour-coded: red = overdue tasks, orange = due today, blue = has meetings.
 * Clicking a day drills into Day mode for that date.
 *
 * @param {object} props
 * @param {dayjs.Dayjs} props.currentDate
 * @param {object} props.data - The /calendar payload.
 * @param {object} props.toggles - { meetings, do_today, time_logs } booleans.
 * @param {function} props.onSelectDay - (dayjs) => void
 */
const MonthGrid = ({ currentDate, data, toggles, onSelectDay }) => {
  const days = useMemo(() => buildMonthDays(currentDate), [currentDate]);

  const meetingsByDay = useMemo(() => {
    const expanded = expandRecurringMeetings(data?.meetings?.events || []);
    const taskEvents = (data?.meetings?.tasks || []).map(taskToMeetingEvent);
    return [...expanded, ...taskEvents];
  }, [data]);

  const cellFor = (day) => {
    const meetingCount = toggles.meetings
      ? meetingsByDay.filter((m) => dayjs(m.start_date).isSame(day, 'day')).length
      : 0;
    const dueTasks = toggles.do_today
      ? (data?.do_today || []).filter((t) => dayjs(t.do_date).isSame(day, 'day'))
      : [];

    // Determine the urgency colour for the day's accent.
    let accent = null;
    if (meetingCount > 0) accent = DAY_COLORS.meeting;
    if (dueTasks.length > 0) {
      if (day.isSame(dayjs(), 'day')) accent = DAY_COLORS.dueToday;
      else if (day.isBefore(dayjs(), 'day')) accent = DAY_COLORS.overdue;
    }
    return { meetingCount, taskCount: dueTasks.length, accent };
  };

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1 }}>
      {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
        <Box key={d} sx={{ p: 0.5, textAlign: 'center', fontWeight: 'bold' }}>
          {d}
        </Box>
      ))}

      {days.map((day) => {
        const { meetingCount, taskCount, accent } = cellFor(day);
        return (
          <Paper
            key={day.format('YYYY-MM-DD')}
            variant="outlined"
            onClick={() => onSelectDay(day)}
            sx={{
              p: 1,
              height: 96,
              cursor: 'pointer',
              opacity: day.isSame(currentDate, 'month') ? 1 : 0.45,
              borderTop: accent ? 3 : 1,
              borderTopColor: accent || 'divider',
              bgcolor: day.isSame(dayjs(), 'day') ? 'action.hover' : 'background.paper',
              '&:hover': { borderColor: 'primary.main' },
            }}
          >
            <Typography variant="caption" sx={{ fontWeight: 'bold' }}>
              {day.format('D')}
            </Typography>
            <Box sx={{ mt: 0.5 }}>
              {toggles.meetings && meetingCount > 0 && (
                <Tooltip title="Meetings">
                  <Typography variant="caption" display="block" sx={{ color: DAY_COLORS.meeting }}>
                    {meetingCount} meeting{meetingCount === 1 ? '' : 's'}
                  </Typography>
                </Tooltip>
              )}
              {toggles.do_today && taskCount > 0 && (
                <Tooltip title="Tasks due">
                  <Typography variant="caption" display="block" color="text.secondary">
                    {taskCount} due
                  </Typography>
                </Tooltip>
              )}
            </Box>
          </Paper>
        );
      })}
    </Box>
  );
};

export default MonthGrid;
